import { humanizeLabel } from '../../lib/humanize-label';
import { severityToTint } from '../../lib/sensitivity-tint';
import { cn } from '../../lib/utils';

/**
 * The wire sensitivity shape: a per-tenant entity label plus a severity score,
 * or `null` when nothing was detected. Re-declared locally (plain primitives) —
 * `packages/ui` must not import `@dawn/feature-config` (agnosticism firewall);
 * this is structurally equal to the wire `AccessLegitimacySensitivity | null`.
 */
export interface GridSensitivity {
  entity: string;
  severity: number;
}

export interface GridSensitivityChipProps {
  /** The detected sensitivity, or `null` to render the explicit "—" placeholder. */
  sensitivity: GridSensitivity | null;
  className?: string;
}

/**
 * `GridSensitivityChip` — the Access Legitimacy grid's own Sensitivity pill
 * (design v6 §3.3). Unlike the shared slug-keyed `SensitivityChip`, this chip is
 * driven by SEVERITY: `severityToTint` picks the orange-ramp tint (lighter =
 * lower severity, darker = higher) and the humanized `entity` is the label text.
 * A `null` sensitivity renders a muted "—" rather than a pill, so "no detected
 * sensitivity" reads as visibly distinct from any level (PRD story 4).
 *
 * Decoupled from the dashboard-wide slug tier system — it never maps a label
 * back to a slug, so it degrades gracefully for any future per-tenant label.
 */
export function GridSensitivityChip({ sensitivity, className }: GridSensitivityChipProps) {
  if (sensitivity === null) {
    return (
      <span
        data-testid="grid-sensitivity-chip-empty"
        className={cn('text-[10px] leading-[1.4] text-grey-400', className)}
      >
        —
      </span>
    );
  }

  const tint = severityToTint(sensitivity.severity);

  return (
    <span
      data-testid="grid-sensitivity-chip"
      data-severity={sensitivity.severity}
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-[4px] px-1.5 py-0.5 text-[10px] font-normal leading-[1.4]',
        tint.background,
        tint.foreground,
        className
      )}
    >
      {humanizeLabel(sensitivity.entity)}
    </span>
  );
}

GridSensitivityChip.displayName = 'GridSensitivityChip';
